import React from 'react';
import {useTheme} from 'styled-components';
import cn from 'clsx';
import {useTranslation} from 'react-i18next';
import {device} from '@guestapp/ui/src/utils/device';
import {useBuildNavigationItems, useScreenResize, useReservationSettings} from 'hooks';
import {getFeatureFlags} from 'utils/features';
import Loader from 'components/common/Loader';
import {ShareButton} from '../ShareButton';
import {ToggleTasksArrow} from './ToggleTasksArrow';
import {NavigationTasksItems} from './NavigationTasksItems';
import {ReservationNumberBox} from './ReservationNumberBox';
import {CheckOutPanelItem} from '../Checkout/CheckOutPanelItem/CheckOutPanelItem';
import {
  BoxTitle,
  Description,
  TaskText,
  StyledSuccessIcon,
  TextAndArrowContainer,
  LoaderWrapper,
} from './styled';

function CheckInInfoBox() {
  const {t} = useTranslation();
  const theme = useTheme();
  const {displayedNavigationItems, isLoading} = useBuildNavigationItems();
  const {showReservationNumberBox} = useReservationSettings();
  const {isMatch: isMobileMode} = useScreenResize(device.laptopM);
  const isCheckOutFeatureEnabled = getFeatureFlags('IT_4407_CHECK_OUT');
  const [isOpenNavigationPanels, setIsOpenNavigationPanels] = React.useState(true);
  const isCompletedTasks =
    !isLoading &&
    displayedNavigationItems.every(navigationItem => navigationItem.completed);

  const totalTasks = displayedNavigationItems.length;
  const completedTask = displayedNavigationItems.filter(
    navigationItem => navigationItem.completed,
  ).length;

  const toggleNavigationItems = React.useCallback(() => {
    setIsOpenNavigationPanels(!isOpenNavigationPanels);
  }, [isOpenNavigationPanels]);
  const showNavigationPanels = isMobileMode ? isOpenNavigationPanels : true;

  const tasksDescriptionTextTrans = isCompletedTasks
    ? t('congrats_your_checkin_is_completed')
    : t('fullfill_pending_task_complete_check_in_before_arrival');

  const taxesTextsStatus = React.useMemo(() => {
    if (!isCompletedTasks) {
      return (
        <>
          <span>{completedTask}</span>/{totalTasks} {t('tasks_completed')}
        </>
      );
    }

    return (
      <>
        <StyledSuccessIcon
          name="successCircle"
          size={16}
          custom
          color={theme.hasTemplate ? theme.colors.branding.primary : '#32D7BE'}
          className="chekin-boxes__tasks-success-icon"
        />
        {t('all_tasks_completed')}
      </>
    );
  }, [theme, completedTask, totalTasks, isCompletedTasks, t]);

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader width={50} />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <div
        className={cn(
          'py-6 px-4 flex flex-col',
          showReservationNumberBox ? 'pt-[54px] md:pt-6 laptop-ml:relative' : '',
        )}
      >
        {showReservationNumberBox && (
          <div className="block laptop-ml:hidden">
            <ReservationNumberBox />
          </div>
        )}
        <BoxTitle className="chekin-boxes__tasks-title">
          {t('check_in_short')}
          <ShareButton />
        </BoxTitle>
        <Description className="chekin-boxes__tasks-description">
          {tasksDescriptionTextTrans}
        </Description>
        <TaskText $marginBottom="8px" className="chekin-boxes__tasks-texts-container">
          <TextAndArrowContainer className="chekin-boxes__tasks-texts-content">
            <TaskText className="chekin-boxes__tasks-status">{taxesTextsStatus}</TaskText>
            {isCompletedTasks && (
              <ToggleTasksArrow
                onClick={toggleNavigationItems}
                isOpenNavigationPanels={isOpenNavigationPanels}
              />
            )}
          </TextAndArrowContainer>
        </TaskText>
        {showNavigationPanels && (
          <NavigationTasksItems showNavigationPanels={showNavigationPanels} />
        )}
      </div>
      {isCheckOutFeatureEnabled && <CheckOutPanelItem />}
    </>
  );
}
export {CheckInInfoBox};
